import { EmptyState, FlexColumn, FlexRow, MaterialIcon } from '@gorila-shared-ui/components';
import { Button } from 'baseui/button';
import { LabelMedium } from 'baseui/typography';
import { memo } from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import { VariableSizeList as List } from 'react-window';
import InfiniteLoader from 'react-window-infinite-loader';
import { DEFAULT_LIST_ITEM_HEIGHT } from '../../../constants/app';
import { useStyles } from '../../../hooks/useStyles';
import { Asset } from '../../../types/asset';
import AssetItem from './AssetItem';

type Props = {
  title: string;
  assets: Asset[];
  assetCount: number;
  isItemLoaded: (index: number) => boolean;
  loadMoreItems: () => void;
  onItemClick: (asset: string) => void;
  onAllClick: () => void;
  emptyStateTitle: string;
  emptyStateDescription?: string;
  type: 'selectable' | 'deselectable';
  totalAssets?: number;
};
function AssetList({
  title,
  assets,
  assetCount,
  isItemLoaded,
  loadMoreItems,
  onItemClick: onClick,
  onAllClick,
  emptyStateTitle,
  emptyStateDescription,
  type,
  totalAssets,
}: Readonly<Props>) {
  const { theme, css, classes } = useStyles();

  return (
    <FlexColumn classNames={`${classes.flexFill} ${css({ gap: theme.sizing.scale300 })}`}>
      <FlexRow classNames={`${css({ alignItems: 'center', justifyContent: 'space-between' })}`}>
        <FlexRow gap={theme.sizing.scale300}>
          <LabelMedium>{title}</LabelMedium>
          {totalAssets !== undefined && (
            <LabelMedium color={theme.colors.contentSecondary}>({totalAssets})</LabelMedium>
          )}
        </FlexRow>
        <Button
          size="mini"
          shape="round"
          kind="tertiary"
          disabled={!assets?.length}
          onClick={onAllClick}
        >
          <MaterialIcon
            name={type === 'selectable' ? 'playlist_add' : 'playlist_remove'}
            size="mini"
          />
        </Button>
      </FlexRow>
      {assets &&
        (assets.length > 0 ? (
          <ul className={css({ margin: 0, paddingLeft: 0, paddingRight: 0, height: '100%' })}>
            <AutoSizer>
              {({ width, height }) => (
                <InfiniteLoader
                  isItemLoaded={isItemLoaded}
                  itemCount={assetCount}
                  loadMoreItems={loadMoreItems}
                >
                  {({ onItemsRendered, ref }) => (
                    <List
                      height={height || 500}
                      itemCount={assetCount}
                      itemSize={() => DEFAULT_LIST_ITEM_HEIGHT}
                      width={width || 200}
                      itemData={{ assets, type, onClick, isLoaded: isItemLoaded }}
                      onItemsRendered={onItemsRendered}
                      overscanCount={4}
                      ref={ref}
                    >
                      {AssetItem}
                    </List>
                  )}
                </InfiniteLoader>
              )}
            </AutoSizer>
          </ul>
        ) : (
          <EmptyState
            title={emptyStateTitle}
            description={emptyStateDescription}
            centered={false}
          />
        ))}
    </FlexColumn>
  );
}

export default memo(AssetList);
